import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { BaseDomainModel } from '../models/base/base-domain-model';
import { exists } from '../functions/exists';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { LocationDomainModel } from './location-domain-model';
import { CompanyDomainModel } from './company-domain-model';
import { UserDomainModel } from './user-domain-model';
import { ProductSignalsRepository } from '../repositories/product-signals/product-signals-repository';
import { FetchOptions } from '../repositories/base/fetch-strategy';
import { ToastService } from '../modules/toasts/service/toast.service';
import { BsError } from '../models/shared/bs-error';
import { HighGuideCompanySignals } from '../models/product/dto/product-signal/high-guide-company-signals';
import { HighGuideProductSignal } from '../models/product/dto/product-signal/high-guide-product-signal';
import { HighGuideProductSignalSnapshot } from '../models/product/dto/product-signal/high-guide-product-signal-snapshot';
import { ProductSignalUser } from '../models/product/dto/product-signal/product-signal-user';
import { Ranking } from '../models/product/dto/product-signal/ranking';
import { WebSocketService } from '../services/web-socket.service';
import { WebSocketMessageContextDataUpdate } from '../models/web-sockets/contexts/web-socket-message-context-data-update';
import { WebSocketMessageContextEvent } from '../models/web-sockets/contexts/web-socket-message-context-event';
import { WebSocketReceiveHighGuideCompanySignalCreated } from '../models/web-sockets/messages/receive/web-socket-receive-high-guide-company-signal-created';
import { WebSocketReceiveHighGuideCompanySignalSnapshot } from '../models/web-sockets/messages/receive/web-socket-receive-high-guide-company-signal-snapshot';

/**
 * HighGuide product signal data for the current user, location and company.
 *
 * Today the only consumer is `HighGuideProductSignalsViewModel`, which relays each stream into the embedded
 * HighGuide iframe. The embedded app cannot fetch this itself: company signals are websocket only and the iframe
 * holds no websocket connection. Recommendation surfaces planned for the dashboard home screen will read the same
 * three streams directly.
 *
 * Every stream resets to `null` on location change. The iframe reads `null` as "loading" and will keep its
 * favorites spinner up or block product sorting until a real value arrives, so a failed fetch must never leave a
 * stream hanging. Each pipeline resolves its own errors rather than propagating them - signals and rankings surface
 * a toast, company signals fail quietly. See `fetchProductSignalsForLocation` for why they are isolated.
 */
// Provided by Logged In Scope
@Injectable()
export class ProductSignalsDomainModel extends BaseDomainModel {
  constructor(
    protected productSignalsRepository: ProductSignalsRepository,
    protected companyDomainModel: CompanyDomainModel,
    protected locationDomainModel: LocationDomainModel,
    protected userDomainModel: UserDomainModel,
    protected toastService: ToastService,
    protected webSocketService: WebSocketService
  ) {
    super();
    this.fetchProductSignalsForLocation();
    this.listenForCompanySignalCreated();
  }

  protected readonly _productSignals = new BehaviorSubject<HighGuideProductSignal[] | null>(null);
  public readonly productSignals$ = this._productSignals as Observable<HighGuideProductSignal[] | null>;

  protected readonly _productRankings = new BehaviorSubject<Ranking[] | null>(null);
  public readonly productRankings$ = this._productRankings as Observable<Ranking[] | null>;

  protected readonly _companySignals = new BehaviorSubject<HighGuideCompanySignals | null>(null);
  public readonly companySignals$ = this._companySignals as Observable<HighGuideCompanySignals | null>;

  /**
   * One variant's refreshed company-level rollup, pushed each time the backend recalculates it behind a new review.
   *
   * An event stream rather than a state bin, and the only one of these four that the dashboard does not hold a copy
   * of: snapshots are fetched per variant on demand by HighGuide itself, so there is no pool here to merge into.
   * The dashboard's whole job is to carry the push across the iframe boundary, which the embedded app cannot
   * receive itself - it holds no websocket connection.
   *
   * Emits the payload verbatim. Only `company` is populated on this push; `global` and `previewRecommendations` are
   * `null` and mean "unchanged". Applying it is the receiver's business.
   */
  public readonly companySignalSnapshotUpdates$ = this.webSocketService
    .getRelevantMessage$<WebSocketReceiveHighGuideCompanySignalSnapshot>(
      WebSocketMessageContextDataUpdate.HighGuideCompanySignalSnapshot
    )
    .pipe(
      map(message => message?.payload),
      filter(snapshot => exists(snapshot?.variantId)),
      shareReplay({ bufferSize: 1, refCount: true })
    ) as Observable<HighGuideProductSignalSnapshot>;

  public getProductSignals$(variantIds?: string[], opts?: FetchOptions): Observable<HighGuideProductSignal[] | null> {
    return this.companyDomainModel.companyId$.pipe(
      take(1),
      switchMap(companyId => {
        return variantIds?.length > 0
          ? this.productSignalsRepository.getProductSignalsByVariantIds$(variantIds)
          : this.productSignalsRepository.getProductSignals$(companyId, opts);
      }),
      tap(productSignals => this._productSignals.next(productSignals))
    );
  }

  public getProductRankings$(opts?: FetchOptions): Observable<Ranking[] | null> {
    return combineLatest([this.companyDomainModel.companyId$, this.locationDomainModel.locationId$]).pipe(
      take(1),
      switchMap(([companyId, locationId]) => {
        return this.productSignalsRepository.getProductRankings$(companyId, locationId, opts);
      }),
      tap(rankings => this._productRankings.next(rankings))
    );
  }

  public getCompanySignals$(opts?: FetchOptions): Observable<HighGuideCompanySignals | null> {
    return combineLatest([
      this.userDomainModel.userId$,
      this.companyDomainModel.companyId$,
      this.locationDomainModel.locationId$
    ]).pipe(
      take(1),
      switchMap(([userId, companyId, locationId]) => {
        return this.productSignalsRepository.getCompanySignals$(userId, companyId, locationId, opts);
      }),
      tap(companySignals => this._companySignals.next(companySignals))
    );
  }

  /**
   * Refetches the signed-in user's own signals, straight from the API with no cache read in front of it.
   *
   * This pool has no way of hearing about its own mutations. The embedded HighGuide app writes signals directly to
   * the API - a review left in the feedback modal, a favorite heart - and the create/update response goes to the
   * iframe, not here, so the pool is stale from that moment until the next location change or page load. The iframe
   * is destroyed on navigation and asks for data again when it remounts, which is the one moment the dashboard can
   * act on. See `HighGuideProductSignalsViewModel.sendDataToIFrame`.
   *
   * `fresh-only` here, unlike the location-change load in `fetchProductSignalsForLocation`, which reads the cache
   * first. The difference is whether a pool already exists: on a location change there is none, so a cached value
   * beats a blank panel. Here one is already on screen and the cache is only ever written from a GET response, so
   * it is stale in exactly the same way the pool is and would emit a redundant value on the way to the fresh one.
   * The strategy still rewrites the cache on success, keeping it in step.
   *
   * Failures stay on the toast path the initial fetch uses, and leave the held pool in place - the iframe keeps
   * relaying what the dashboard already had rather than dropping to a loading state.
   */
  refreshProductSignals(): void {
    const fresh$ = this.companyDomainModel.companyId$.pipe(
      take(1),
      switchMap(companyId => {
        return this.productSignalsRepository.getProductSignals$(companyId, { strategy: 'fresh-only' });
      }),
      tap(productSignals => this._productSignals.next(productSignals))
    );
    this.reportErrors$(fresh$).subscribeWhileAlive({ owner: this });
  }

  /**
   * Each stream resolves its own failure - signals and rankings via `reportErrors$` (toast), company signals
   * quietly. `merge` would otherwise propagate the first error out through the `switchMap` and terminate
   * `locationId$`, which would silently stop *all three* from ever refetching again, including on later location
   * changes. Isolating them keeps one flaky read from disabling the other two.
   */
  fetchProductSignalsForLocation(): void {
    this.locationDomainModel.locationId$
      .pipe(
        tap(_ => {
          this._productSignals.next(null);
          this._productRankings.next(null);
          this._companySignals.next(null);
        }),
        distinctUntilChanged(),
        debounceTime(100),
        switchMap(locationId => {
          if (!locationId) return of(null);
          // cache-then-fresh: a location switch is a complete reload of this scope, and all three pools were
          // blanked above, so each cached emission fills a UI already showing its loading state.
          const warmStart: FetchOptions = { strategy: 'cache-then-fresh' };
          return merge(
            this.reportErrors$(this.getProductSignals$(null, warmStart)),
            this.reportErrors$(this.getProductRankings$(warmStart)),
            // Supplementary context with no REST fallback to retry against, so a failure stays quiet rather than
            // nagging the user about teammates' notes. The subject keeps whatever it last received: `null` on a
            // cold cache, or the cached value `cache-then-fresh` already emitted.
            this.getCompanySignals$(warmStart).pipe(catchError(() => of(null)))
          );
        })
      )
      .subscribeWhileAlive({ owner: this });
  }

  /**
   * Teammates' recommendations created while this session is open arrive one at a time and are folded into the pool
   * the full grab already populated, so the HighGuide iframe sees them without a refetch or a refresh. The event
   * carries the same `HighGuideCompanySignals` shape as that grab, holding the single new signal plus its author.
   *
   * Merging into `_companySignals` is the whole delivery mechanism for the embedded app: the relay view model is
   * subscribed to `companySignals$`, so the merged pool reaches the iframe over the channel it already listens on.
   *
   * A `null` pool means the initial grab has not landed yet, which the iframe reads as "still loading". The
   * incremental signal is dropped in that window rather than painting a one-signal pool as loaded - the in-flight
   * grab is a fresh server read and comes back holding it.
   */
  private listenForCompanySignalCreated(): void {
    this.webSocketService
      .getRelevantMessage$<WebSocketReceiveHighGuideCompanySignalCreated>(
        WebSocketMessageContextEvent.HighGuideCompanySignalCreated
      )
      .subscribeWhileAlive({
        owner: this,
        next: message => this.mergeCompanySignals(message?.payload)
      });
  }

  private mergeCompanySignals(incoming: HighGuideCompanySignals): void {
    if (!incoming) return;
    this._companySignals.once(curr => {
      if (!curr) return;
      const merged = window?.injector?.Deserialize?.shallowCopyOf(curr);
      merged.users = ProductSignalsDomainModel.mergeUsers(curr.users, incoming.users);
      merged.signalsByVariantId = ProductSignalsDomainModel.mergeSignalsByVariantId(
        curr.signalsByVariantId,
        incoming.signalsByVariantId
      );
      this._companySignals.next(merged);
    });
  }

  /**
   * Keyed by `userId` so a teammate already in the roster is refreshed rather than duplicated. The shared
   * `anonymous` sentinel collapses onto a single entry by the same rule, which is correct - it is one placeholder
   * standing in for every unresolvable author, not a person.
   */
  private static mergeUsers(curr: ProductSignalUser[], incoming: ProductSignalUser[]): ProductSignalUser[] {
    const merged = curr?.shallowCopy() ?? [];
    incoming?.forEach(user => {
      if (!user?.userId) return;
      const index = merged.findIndex(it => it?.userId === user.userId);
      index > -1 ? merged.splice(index, 1, user) : merged.push(user);
    });
    return merged;
  }

  /**
   * Delivery is at-least-once and a signal may already be held from the full grab, so an incoming row replaces the
   * one it matches rather than stacking on top of it. See `HighGuideProductSignal.isSameSignalAs` for what counts
   * as the same signal.
   */
  private static mergeSignalsByVariantId(
    curr: Map<string, HighGuideProductSignal[]>,
    incoming: Map<string, HighGuideProductSignal[]>
  ): Map<string, HighGuideProductSignal[]> {
    const merged = curr?.shallowCopy() ?? new Map<string, HighGuideProductSignal[]>();
    incoming?.forEach((signals, variantId) => {
      const bucket = merged.get(variantId)?.shallowCopy() ?? [];
      signals?.forEach(signal => {
        if (!signal?.variantId) return;
        const index = bucket.findIndex(it => it?.isSameSignalAs(signal));
        index > -1 ? bucket.splice(index, 1, signal) : bucket.push(signal);
      });
      merged.set(variantId, bucket);
    });
    return merged;
  }

  private reportErrors$<T>(source$: Observable<T>): Observable<T | null> {
    return source$.pipe(
      catchError((err: BsError) => {
        this.toastService.publishError(err);
        return of(null);
      })
    );
  }
}
